window.UmageInterior = (function () {
  const STORAGE_KEY = 'umage_my_interior_v2';
  const SEEN_KEY = 'umage_my_interior_seen_v1';
  const UPDATED_EVENT = 'umage:interior:updated';
  let isInitialized = false;

  const CATEGORY_MAP = {
    Seating: ['stool', 'sofa', 'chair', 'daybed'],
    Lighting: ['lamp'],
    Storage: ['cabinet', 'highboard', 'dresser', 'shelf', 'shelving', 'wall hanger', 'mirror', 'hook'],
    Tables: ['table', 'bench', 'sideboard', 'desk']
  };

  const CATEGORY_ACTIONS = {
    Seating: [
      { label: 'Lounge chairs', url: '/collections/lounge-chairs' },
      { label: 'Dining chairs', url: '/collections/dining-chairs' },
      { label: 'Sofas & daybeds', url: '/collections/sofas-daybeds' }
    ],
    Lighting: [
      { label: 'Explore lighting', url: '/collections/lighting' }
    ],
    Tables: [
      { label: 'Coffee & side tables', url: '/collections/coffee-side-tables' },
      { label: 'Dining tables', url: '/collections/dining-tables' },
      { label: 'Desks & console tables', url: '/collections/desks-console-tables' }
    ],
    Storage: [
      { label: 'Cabinets & shelving', url: '/collections/cabinets-shelving' }
    ]
  };

  const RECOMMENDATION_HANDLES = {
    Seating: [
      'the-reader-wing-chair',
      'a-conversation-piece-dining-chair',
      'curious-dining-chair',
      'cushion-accessory-for-paff-bench',
      'heiko'
    ],
    Lighting: [
      'eos',
      'asteria-move-portable-lamp',
      'asteria-pendant',
      'spring-table-lamp',
      'omni-floor'
    ],
    Tables: [
      'comfort-circle-dining-table',
      'audacious-side-table',
      'together-60-smooth-square'
    ],
    Storage: [
      'audacious-sideboard',
      'treasures-3-door-cabinet',
      'treasures-dresser'
    ]
  };

  const DEFAULT_RECOMMENDATION_ORDER = [
    'omni-floor',
    'spring-table-lamp',
    'my-spot-side-table'
  ];

  const PROFILE_COPY = {
    calm: {
      title: 'The Calm Curator',
      text: 'You are drawn to pieces that soften the room and bring a sense of ease to everyday life. Your saved styles suggest a home shaped around atmosphere, comfort and quiet beauty.'
    },
    social: {
      title: 'The Social Stylist',
      text: 'Your choices suggest a warm and welcoming interior. You seem to gravitate towards pieces that make space for connection, conversation and beautiful everyday moments shared with others.'
    },
    practical: {
      title: 'The Practical Aesthete',
      text: 'You value design that works beautifully in real life. Your interior direction feels considered, useful and elegant — with room for both visual calm and everyday function.'
    },
    modernist: {
      title: 'The Soft Modernist',
      text: 'Your selection points to a refined and contemporary eye. You are drawn to sculptural silhouettes, clean lines and a softened modern expression that feels both timeless and inviting.'
    },
    host: {
      title: 'The Collected Host',
      text: 'Your saved pieces suggest a home with polish, warmth and personality. You seem to be building spaces that feel intentional, memorable and naturally welcoming.'
    }
  };

  function normalizeHandle(handle) {
    return String(handle || '').trim();
  }

  function normalizeVariantId(variantId) {
    if (variantId === null || variantId === undefined || variantId === '') return '';
    return String(variantId).trim();
  }

  function normalizeType(type) {
    return String(type || '').trim().toLowerCase();
  }

  function buildItemKey(handle, variantId) {
    const normalizedVariantId = normalizeVariantId(variantId);
    const normalizedHandle = normalizeHandle(handle);

    if (normalizedVariantId) return `variant:${normalizedVariantId}`;
    return `handle:${normalizedHandle}`;
  }

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(item => item && typeof item.handle === 'string' && item.handle.trim() !== '');
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      localStorage.setItem(SEEN_KEY, 'true');
      emit(items);
      return true;
    } catch (e) {
      return false;
    }
  }

  function emit(items) {
    window.dispatchEvent(
      new CustomEvent(UPDATED_EVENT, {
        detail: Array.isArray(items) ? items : read()
      })
    );
  }

  function getItems() {
    return read();
  }

  function isSaved(handle, variantId) {
    const key = buildItemKey(handle, variantId);
    return read().some(item => item.key === key);
  }

  function add(handle, variantId) {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return false;

    const normalizedVariantId = normalizeVariantId(variantId);
    const key = buildItemKey(normalizedHandle, normalizedVariantId);
    const items = read();

    if (!items.some(item => item.key === key)) {
      items.push({
        key,
        handle: normalizedHandle,
        variantId: normalizedVariantId,
        addedAt: Date.now()
      });
      write(items);
    }

    return true;
  }

  function remove(handle, variantId) {
    const key = buildItemKey(handle, variantId);
    const items = read().filter(item => item.key !== key);
    write(items);
    return false;
  }

  function clearAll() {
    write([]);
  }

  function toggle(handle, variantId) {
    return isSaved(handle, variantId) ? remove(handle, variantId) : add(handle, variantId);
  }

  function setButtonState(button, saved) {
    if (!button) return;
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('data-my-interior-state', saved ? 'saved' : 'unsaved');
  }

  function syncButtons() {
    document.querySelectorAll('[data-my-interior-toggle]').forEach(button => {
      const handle = normalizeHandle(button.getAttribute('data-product-handle'));
      const variantId = normalizeVariantId(button.getAttribute('data-product-variant-id'));
      const saved = handle ? isSaved(handle, variantId) : false;
      setButtonState(button, saved);
    });
  }

  function updateLiveRegions(message) {
    document.querySelectorAll('[data-my-interior-live]').forEach(live => {
      live.textContent = '';
      window.setTimeout(() => {
        live.textContent = message;
      }, 40);
    });
  }

  function runBurst(button) {
    if (!button || !button.hasAttribute('data-heart-burst')) return;

    button.classList.remove('is-bursting');
    void button.offsetWidth;
    button.classList.add('is-bursting');

    window.setTimeout(() => {
      button.classList.remove('is-bursting');
    }, 650);
  }

  function classifyCategories(product) {
    const type = normalizeType(product.type);
    const categories = [];

    Object.keys(CATEGORY_MAP).forEach(category => {
      const matched = CATEGORY_MAP[category].some(keyword => type.includes(keyword));
      if (matched) categories.push(category);
    });

    return categories;
  }

  function getChecklist(products) {
    const present = {
      Seating: false,
      Lighting: false,
      Tables: false,
      Storage: false
    };

    products.forEach(product => {
      classifyCategories(product).forEach(category => {
        present[category] = true;
      });
    });

    return present;
  }

  function getMissingCategories(checklist) {
    return Object.keys(checklist).filter(category => !checklist[category]);
  }

  function shouldAllowPouffe(savedProducts) {
    return savedProducts.some(product => normalizeHandle(product.handle).toLowerCase().includes('lounge-around'));
  }

  function scoreProfiles(products) {
    const score = {
      calm: 0,
      social: 0,
      practical: 0,
      modernist: 0,
      host: 0
    };

    products.forEach(product => {
      const handle = normalizeHandle(product.handle).toLowerCase();
      const categories = classifyCategories(product);

      if (categories.includes('Lighting')) {
        score.calm += 2;
        score.modernist += 2;
        score.host += 1;
      }

      if (categories.includes('Seating')) {
        score.calm += 2;
        score.social += 2;
      }

      if (categories.includes('Tables')) {
        score.social += 2;
        score.host += 2;
        score.practical += 1;
      }

      if (categories.includes('Storage')) {
        score.practical += 3;
        score.modernist += 1;
      }

      if (handle.includes('lounge-around') || handle.includes('reader') || handle.includes('omni-floor')) {
        score.calm += 3;
      }

      if (handle.includes('conversation') || handle.includes('comfort-circle') || handle.includes('curious')) {
        score.social += 3;
        score.host += 1;
      }

      if (handle.includes('treasures') || handle.includes('audacious-desk') || handle.includes('hang-in-there')) {
        score.practical += 3;
      }

      if (handle.includes('asteria') || handle.includes('eos') || handle.includes('spring-table-lamp')) {
        score.modernist += 2;
      }

      if (handle.includes('audacious-side-table') || handle.includes('sideboard') || handle.includes('dining-chair')) {
        score.host += 2;
      }
    });

    const winner = Object.entries(score).sort((a, b) => b[1] - a[1])[0];
    return winner ? winner[0] : 'calm';
  }

  async function fetchProduct(handle) {
    const response = await fetch('/products/' + encodeURIComponent(handle) + '.js');
    if (!response.ok) throw new Error('Product fetch failed');
    return response.json();
  }

  function getVariantForSavedItem(product, savedItem) {
    if (!product || !product.variants || !product.variants.length) return null;

    if (savedItem.variantId) {
      const exact = product.variants.find(variant => String(variant.id) === String(savedItem.variantId));
      if (exact) return exact;
    }

    return product.variants.find(variant => variant.available) || product.variants[0];
  }

  function normalizeSavedProduct(product, savedItem) {
    const variant = getVariantForSavedItem(product, savedItem);
    const variantImage =
      variant && variant.featured_image
        ? variant.featured_image.src || variant.featured_image
        : '';
    const image = variantImage || product.featured_image || '';

    return {
      key: savedItem.key,
      handle: product.handle,
      title: product.title,
      url: product.url,
      image,
      price: variant ? variant.price : product.price,
      type: product.type || '',
      variantId: variant ? String(variant.id) : '',
      variantTitle: variant && variant.title !== 'Default Title' ? variant.title : '',
      variantAvailable: variant ? !!variant.available : false
    };
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function getCurrencyCode(fallback) {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return fallback || '';
  }

  function formatMoney(cents, moneyFormat, currencyCode) {
    let value = '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      value = window.Shopify.formatMoney(cents, moneyFormat);
    } else {
      value = (Number(cents || 0) / 100).toFixed(2);
    }

    const code = getCurrencyCode(currencyCode);
    return code ? `${value} ${code}` : value;
  }

  function progressHeading(missingCategories) {
    if (!missingCategories.length) {
      return 'Your room feels beautifully balanced';
    }

    if (missingCategories.length === 1) {
      return `You may still be missing ${missingCategories[0].toLowerCase()}`;
    }

    return 'A few more layers could complete the expression';
  }

  function progressMessage(missingCategories) {
    if (!missingCategories.length) {
      return 'You have created a room that feels beautifully complete and in balance.';
    }

    if (missingCategories.length === 1) {
      return `You are very close — adding ${missingCategories[0].toLowerCase()} could complete the room.`;
    }

    if (missingCategories.length === 2) {
      return `A few final layers could complete the feeling — especially ${missingCategories[0].toLowerCase()} and ${missingCategories[1].toLowerCase()}.`;
    }

    return 'Great taste. A few more considered pieces could help complete the room and make it feel fully resolved.';
  }

  function buildRecommendationHandles(savedProducts, profileKey, missingCategories, hasSavedProducts) {
    if (!hasSavedProducts) {
      return DEFAULT_RECOMMENDATION_ORDER.slice();
    }

    const savedHandles = savedProducts.map(product => normalizeHandle(product.handle).toLowerCase());
    const canShowPouffe = shouldAllowPouffe(savedProducts);

    let handles = [];

    missingCategories.forEach(category => {
      handles = handles.concat(RECOMMENDATION_HANDLES[category] || []);
    });

    if (!handles.length) {
      Object.keys(RECOMMENDATION_HANDLES).forEach(category => {
        handles = handles.concat(RECOMMENDATION_HANDLES[category] || []);
      });
    }

    if (profileKey === 'calm') {
      handles = handles.concat(['omni-floor', 'spring-table-lamp', 'the-reader-wing-chair']);
    }

    if (profileKey === 'social') {
      handles = handles.concat(['comfort-circle-dining-table', 'a-conversation-piece-dining-chair', 'curious-dining-chair']);
    }

    if (profileKey === 'practical') {
      handles = handles.concat(['treasures-dresser', 'audacious-sideboard', 'hang-in-there']);
    }

    if (profileKey === 'modernist') {
      handles = handles.concat(['asteria-pendant', 'eos', 'audacious-desk']);
    }

    if (profileKey === 'host') {
      handles = handles.concat(['comfort-circle-dining-table', 'audacious-side-table', 'treasures-3-door-cabinet']);
    }

    handles = Array.from(new Set(handles)).filter(handle => !savedHandles.includes(handle.toLowerCase()));

    if (!canShowPouffe) {
      handles = handles.filter(handle => !handle.toLowerCase().includes('pouffe'));
    }

    return handles;
  }

  function renderProgressButtons(missingCategories) {
    if (!missingCategories.length) return '';

    const progressCopy = {
      Seating: 'Introduce a piece that invites pause, comfort and conversation.',
      Lighting: 'Layer the room with glow and atmosphere to complete the mood.',
      Tables: 'Add surfaces that make everyday rituals feel more effortless.',
      Storage: 'Bring calm to the room with elegant storage that keeps things close, but out of sight.'
    };

    return missingCategories.map(category => {
      const actions = CATEGORY_ACTIONS[category] || [];
      if (!actions.length) return '';

      return `
        <div class="my-interior-progress-card">
          <div class="my-interior-progress-card__header">
            <div class="my-interior-progress-card__eyebrow">This could evolve your home</div>
            <h3 class="my-interior-progress-card__title">${escapeHtml(category)}</h3>
            <p class="my-interior-progress-card__copy">${escapeHtml(progressCopy[category] || '')}</p>
          </div>
          <div class="my-interior-progress-card__buttons">
            ${actions.map(action => `
              <a class="my-interior-progress-card__button" href="${escapeHtml(action.url)}">
                ${escapeHtml(action.label)}
              </a>
            `).join('')}
          </div>
        </div>
      `;
    }).join('');
  }

  function renderChecklist(checklist) {
    return Object.keys(checklist).map(category => {
      const done = checklist[category];

      return `
        <div class="my-interior__check-item ${done ? 'is-complete' : 'is-missing'}">
          <span class="my-interior__check-icon" aria-hidden="true">${done ? '✓' : '✕'}</span>
          <span class="my-interior__check-label">${escapeHtml(category)}</span>
        </div>
      `;
    }).join('');
  }

  function renderSavedCard(product, moneyFormat, currencyCode) {
    const priceHtml = typeof product.price === 'number'
      ? `<div class="my-interior-saved__price">${escapeHtml(formatMoney(product.price, moneyFormat, currencyCode))}</div>`
      : '';

    const variantHtml = product.variantTitle
      ? `<div class="my-interior-saved__variant">${escapeHtml(product.variantTitle)}</div>`
      : '';

    const addDisabled = !product.variantId || !product.variantAvailable ? 'disabled' : '';

    return `
      <article class="my-interior-saved" data-product-key="${escapeHtml(product.key)}">
        <button
          type="button"
          class="my-interior-saved__remove"
          data-remove-product
          data-product-handle="${escapeHtml(product.handle)}"
          data-product-variant-id="${escapeHtml(product.variantId)}"
          aria-label="Remove ${escapeHtml(product.title)} from My Interior"
        ></button>

        <button
          type="button"
          class="my-interior-saved__media-toggle"
          data-saved-toggle
          aria-expanded="true"
          aria-label="${escapeHtml(product.title)}"
        >
          <img class="my-interior-saved__image" src="${escapeHtml(product.image + '&width=900')}" alt="${escapeHtml(product.title)}">
        </button>

        <div class="my-interior-saved__overlay" data-saved-overlay>
          <div class="my-interior-saved__overlay-inner">
            <div class="my-interior-saved__meta">
              <h3 class="my-interior-saved__title">${escapeHtml(product.title)}</h3>
              ${variantHtml}
              ${priceHtml}
            </div>

            <div class="my-interior-saved__actions">
              <button class="my-interior-saved__button my-interior-saved__button--dark" type="button" data-add-to-cart data-variant-id="${escapeHtml(product.variantId)}" ${addDisabled}>
                ${product.variantAvailable ? 'Add to cart' : 'Unavailable'}
              </button>
              <a class="my-interior-saved__button my-interior-saved__button--light" href="${escapeHtml(product.url)}">Explore</a>
            </div>
          </div>
        </div>
      </article>
    `;
  }

  function renderRecommendationCard(product, moneyFormat, currencyCode) {
    const priceHtml = typeof product.price === 'number'
      ? `<div class="my-interior-reco__price">${escapeHtml(formatMoney(product.price, moneyFormat, currencyCode))}</div>`
      : '';

    return `
      <article class="my-interior-reco" data-recommendation-handle="${escapeHtml(product.handle)}" data-recommendation-variant-id="${escapeHtml(product.variantId || '')}">
        <a class="my-interior-reco__media" href="${escapeHtml(product.url)}">
          <img class="my-interior-reco__image" src="${escapeHtml(product.image + '&width=700')}" alt="${escapeHtml(product.title)}">
        </a>
        <div class="my-interior-reco__body">
          <h3 class="my-interior-reco__title"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3>
          <div class="my-interior-reco__type">${escapeHtml(product.type || '')}</div>
          ${priceHtml}
          <div class="my-interior-reco__actions">
            <button
              type="button"
              class="my-interior-reco__button"
              data-my-interior-toggle
              data-heart-burst
              data-product-handle="${escapeHtml(product.handle)}"
              data-product-variant-id="${escapeHtml(product.variantId || '')}"
              aria-pressed="false"
            >
              Save to My Interior
            </button>
            <a class="my-interior-saved__button my-interior-saved__button--light" href="${escapeHtml(product.url)}">Explore</a>
          </div>
        </div>
      </article>
    `;
  }

  async function addToCart(variantId, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: Number(quantity)
      })
    });

    if (!response.ok) {
      throw new Error('Cart add failed');
    }

    return response.json();
  }

  async function addManyToCart(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: items.map(item => ({
          id: Number(item.id),
          quantity: Number(item.quantity || 1)
        }))
      })
    });

    if (!response.ok) {
      throw new Error('Cart add many failed');
    }

    return response.json();
  }

  function refreshAndOpenCartDrawer() {
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:refresh', { bubbles: true })
  );

  window.setTimeout(function () {
    const openDrawerButton = document.querySelector('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]');

    if (openDrawerButton) {
      openDrawerButton.click();
      return;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('drawer:open', {
        bubbles: true,
        detail: { id: 'sidebar-cart' }
      })
    );
  }, 250);
}

  async function renderPage(section) {
    if (!section) return;

    const emptyHero = section.querySelector('[data-mi-empty-hero]');
    const savedZone = section.querySelector('[data-mi-saved-zone]');
    const progressZone = section.querySelector('[data-mi-progress-zone]');
    const profileZone = section.querySelector('[data-mi-profile-zone]');
    const savedGrid = section.querySelector('[data-mi-grid]');
    const progressHeadingEl = section.querySelector('[data-mi-progress-heading]');
    const encouragementText = section.querySelector('[data-mi-encouragement]');
    const checklistWrap = section.querySelector('[data-mi-checklist]');
    const progressButtonsWrap = section.querySelector('[data-mi-progress-buttons]');
    const completeArea = section.querySelector('[data-mi-complete-area]');
    const recommendationsWrap = section.querySelector('[data-mi-recommendations-grid]');
    const recommendationsHeading = section.querySelector('[data-mi-recommendations-heading]');
    const recommendationsCopy = section.querySelector('[data-mi-recommendations-copy]');
    const profileTitle = section.querySelector('[data-mi-profile-title]');
    const profileText = section.querySelector('[data-mi-profile-text]');
    const introWrap = section.querySelector('[data-mi-intro-wrap]');
    const savedCountEl = section.querySelector('[data-mi-saved-count]');
    const addAllButton = section.querySelector('[data-mi-add-all]');
    const clearAllButton = section.querySelector('[data-mi-clear-all]');
    const moneyFormat = section.getAttribute('data-money-format') || '';
    const currencyCode = section.getAttribute('data-currency-code') || '';

    const savedItems = getItems();
    const hasSavedProducts = savedItems.length > 0;

    if (emptyHero) emptyHero.hidden = hasSavedProducts;
    if (savedZone) savedZone.hidden = !hasSavedProducts;
    if (progressZone) progressZone.hidden = !hasSavedProducts;
    if (profileZone) profileZone.hidden = !hasSavedProducts;
    if (introWrap) introWrap.hidden = false;

    if (!hasSavedProducts) {
      if (savedCountEl) {
        savedCountEl.textContent = '';
      }

      if (recommendationsHeading) {
        recommendationsHeading.textContent = 'A few pieces to begin with';
      }

      if (recommendationsCopy) {
        recommendationsCopy.textContent = 'A small selection of considered designs to help you start shaping your interior.';
      }

      const defaultRecommendationProductsFetched = await Promise.all(
        DEFAULT_RECOMMENDATION_ORDER.map(async handle => {
          try {
            const product = await fetchProduct(handle);
            const variant = product.variants && product.variants.length ? (product.variants.find(v => v.available) || product.variants[0]) : null;

            return {
              handle: product.handle,
              title: product.title,
              url: product.url,
              image: (variant && variant.featured_image && (variant.featured_image.src || variant.featured_image)) || product.featured_image || '',
              price: variant ? variant.price : product.price,
              type: product.type || '',
              variantId: variant ? String(variant.id) : ''
            };
          } catch (error) {
            return null;
          }
        })
      );

      const defaultRecommendations = defaultRecommendationProductsFetched.filter(Boolean).slice(0, 4);

      if (recommendationsWrap) {
        recommendationsWrap.innerHTML = defaultRecommendations
          .map(product => renderRecommendationCard(product, moneyFormat, currencyCode))
          .join('');
      }

      syncButtons();
      return;
    }

    const fetched = await Promise.all(
      savedItems.map(async savedItem => {
        try {
          const product = await fetchProduct(savedItem.handle);
          return normalizeSavedProduct(product, savedItem);
        } catch (error) {
          return null;
        }
      })
    );

    const savedProducts = fetched.filter(Boolean);
    if (!savedProducts.length) return;

    if (savedCountEl) {
      const count = savedProducts.length;
      savedCountEl.textContent = count === 1 ? '1 saved style' : `${count} saved styles`;
    }

    const profileKey = scoreProfiles(savedProducts);
    const profile = PROFILE_COPY[profileKey] || PROFILE_COPY.calm;
    const checklist = getChecklist(savedProducts);
    const missingCategories = getMissingCategories(checklist);

    if (profileTitle) profileTitle.textContent = profile.title;
    if (profileText) profileText.textContent = profile.text;
    if (checklistWrap) checklistWrap.innerHTML = renderChecklist(checklist);
    if (progressHeadingEl) progressHeadingEl.textContent = progressHeading(missingCategories);
    if (encouragementText) encouragementText.textContent = progressMessage(missingCategories);

    if (progressButtonsWrap) {
      progressButtonsWrap.innerHTML = renderProgressButtons(missingCategories);
      progressButtonsWrap.hidden = !missingCategories.length;
    }

    if (completeArea) {
      completeArea.hidden = !!missingCategories.length;
    }

    if (savedGrid) {
      savedGrid.innerHTML = savedProducts.map(product => renderSavedCard(product, moneyFormat, currencyCode)).join('');
    }

    if (addAllButton) {
      addAllButton.disabled = !savedProducts.some(product => product.variantId && product.variantAvailable);
    }

    if (clearAllButton) {
      clearAllButton.disabled = false;
    }

    const recommendationHandles = buildRecommendationHandles(savedProducts, profileKey, missingCategories, true);

    const recoFetched = await Promise.all(
      recommendationHandles.slice(0, 8).map(async handle => {
        try {
          const product = await fetchProduct(handle);
          const variant = product.variants && product.variants.length ? (product.variants.find(v => v.available) || product.variants[0]) : null;

          return {
            handle: product.handle,
            title: product.title,
            url: product.url,
            image: (variant && variant.featured_image && (variant.featured_image.src || variant.featured_image)) || product.featured_image || '',
            price: variant ? variant.price : product.price,
            type: product.type || '',
            variantId: variant ? String(variant.id) : ''
          };
        } catch (error) {
          return null;
        }
      })
    );

    const recommendations = recoFetched.filter(Boolean).slice(0, 4);

    if (recommendationsHeading) {
      recommendationsHeading.textContent = 'Based on your style — You might also like';
    }

    if (recommendationsCopy) {
      recommendationsCopy.textContent = 'A few considered suggestions that complement the direction already taking shape.';
    }

    if (recommendationsWrap) {
      recommendationsWrap.innerHTML = recommendations
        .map(product => renderRecommendationCard(product, moneyFormat, currencyCode))
        .join('');
    }

    if (savedGrid) {
      savedGrid.querySelectorAll('[data-add-to-cart]').forEach(button => {
        button.addEventListener('click', async function (event) {
          event.stopPropagation();

          const variantId = normalizeVariantId(button.getAttribute('data-variant-id'));
          if (!variantId) return;

          button.disabled = true;
          button.textContent = 'Adding…';

          try {
await addToCart(variantId, 1);
refreshAndOpenCartDrawer();
updateLiveRegions('Product added to cart.');
button.textContent = 'Added';
          } catch (error) {
            updateLiveRegions('Something went wrong while adding the product to cart.');
            button.textContent = 'Add to cart';
          }

          window.setTimeout(() => {
            button.disabled = false;
            button.textContent = 'Add to cart';
          }, 1000);
        });
      });

      savedGrid.querySelectorAll('[data-remove-product]').forEach(button => {
        button.addEventListener('click', function (event) {
          event.stopPropagation();

          const handle = normalizeHandle(button.getAttribute('data-product-handle'));
          const variantId = normalizeVariantId(button.getAttribute('data-product-variant-id'));
          const card = button.closest('.my-interior-saved');

          if (card) {
            card.classList.add('is-removing');
            window.setTimeout(() => {
              remove(handle, variantId);
              syncButtons();
              renderPage(section);
              updateLiveRegions('Product removed from My Interior.');
            }, 260);
          } else {
            remove(handle, variantId);
            syncButtons();
            renderPage(section);
            updateLiveRegions('Product removed from My Interior.');
          }
        });
      });
    }

    if (addAllButton) {
      addAllButton.onclick = async function () {
        const items = savedProducts
          .filter(product => product.variantId && product.variantAvailable)
          .map(product => ({
            id: Number(product.variantId),
            quantity: 1
          }));

        if (!items.length) return;

        addAllButton.disabled = true;
        addAllButton.textContent = 'Adding all…';

        try {
await addManyToCart(items);
refreshAndOpenCartDrawer();
updateLiveRegions('All available saved products have been added to cart.');
addAllButton.textContent = 'Added';
        } catch (error) {
          updateLiveRegions('Something went wrong while adding all products to cart.');
          addAllButton.textContent = 'Add all to cart';
        }

        window.setTimeout(() => {
          addAllButton.disabled = false;
          addAllButton.textContent = 'Add all to cart';
        }, 1200);
      };
    }

    if (clearAllButton) {
      clearAllButton.onclick = function () {
        clearAll();
        syncButtons();
        renderPage(section);
        updateLiveRegions('My Interior has been cleared.');
      };
    }

    syncButtons();
  }

  function handleToggleClick(button) {
    const handle = normalizeHandle(button.getAttribute('data-product-handle'));
    const variantId = normalizeVariantId(button.getAttribute('data-product-variant-id'));
    if (!handle) return;

    const saved = toggle(handle, variantId);
    syncButtons();

    if (saved) {
      runBurst(button);
      updateLiveRegions('Product saved to My Interior.');
    } else {
      updateLiveRegions('Product removed from My Interior.');
    }

    document.querySelectorAll('[data-my-interior-page]').forEach(section => {
      renderPage(section);
    });
  }

  function bindDocumentClick() {
    document.addEventListener('click', function (event) {
      const button = event.target.closest('[data-my-interior-toggle]');
      if (!button) return;

      event.preventDefault();
      handleToggleClick(button);
    });
  }

  function bindStorageSync() {
    window.addEventListener('storage', function (event) {
      if (event.key === STORAGE_KEY) {
        syncButtons();
        emit(read());
      }
    });
  }

  function bindCustomSync() {
    window.addEventListener(UPDATED_EVENT, function () {
      syncButtons();
      document.querySelectorAll('[data-my-interior-page]').forEach(section => {
        renderPage(section);
      });
    });
  }

  function init() {
    if (isInitialized) {
      syncButtons();
      document.querySelectorAll('[data-my-interior-page]').forEach(section => {
        renderPage(section);
      });
      return;
    }

    isInitialized = true;
    bindDocumentClick();
    bindStorageSync();
    bindCustomSync();
    syncButtons();

    document.querySelectorAll('[data-my-interior-page]').forEach(section => {
      renderPage(section);
    });
  }

  return {
    init,
    getItems,
    isSaved,
    add,
    remove,
    clearAll,
    toggle,
    syncButtons,
    renderPage
  };
})();

document.addEventListener('DOMContentLoaded', function () {
  if (window.UmageInterior) {
    window.UmageInterior.init();
  }
});